import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GmailAccount } from '../../types/gmail';
import {
  activateGmailAccount,
  connectGmailAccount,
  deactivateGmailAccount,
  disconnectGmailAccount,
  getGmailAccounts,
  getSkippedEmails,
} from '../../services/gmailApi';
import { Button } from '../ui/Button';
import { SettingsSection, SettingsList, SettingsListItem } from '../settings/ui';
import styles from './GmailSettings.module.css';

export interface GmailSettingsProps {
  embedded?: boolean;
}

export const GmailSettings: React.FC<GmailSettingsProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<GmailAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [reviewQueueCount, setReviewQueueCount] = useState<number | null>(null);
  const [reviewQueueLoading, setReviewQueueLoading] = useState(true);
  const [reviewQueueError, setReviewQueueError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const errorParam = params.get('error');

    if (success) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (errorParam) {
      setError(getErrorMessage(errorParam));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const getErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case 'oauth_denied':
        return 'Gmail access was denied. Please try again and grant the required permissions.';
      case 'missing_code':
        return 'Authorization failed. Please try again.';
      case 'invalid_state':
        return 'Session expired. Please try connecting again.';
      case 'connection_failed':
        return 'Failed to connect Gmail account. Please try again.';
      default:
        return 'An error occurred. Please try again.';
    }
  };

  const fetchAccounts = useCallback(async () => {
    try {
      setAccountsLoading(true);
      setAccountsError(null);

      const accountData = await getGmailAccounts();
      setAccounts(accountData);
      setAccountsError(null);
    } catch (err) {
      console.error('Error fetching Gmail accounts:', err);
      setAccountsError('Failed to load Gmail accounts');
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  const fetchReviewQueueCount = useCallback(async () => {
    try {
      setReviewQueueLoading(true);
      setReviewQueueError(null);

      const response = await getSkippedEmails({
        limit: 1,
        offset: 0,
      });
      setReviewQueueCount(response.total);
      setReviewQueueError(null);
    } catch (err) {
      console.error('Error fetching skipped email review queue count:', err);
      setReviewQueueError('Failed to load skipped email review queue');
    } finally {
      setReviewQueueLoading(false);
    }
  }, []);

  const refreshSettingsData = useCallback(async () => {
    await Promise.allSettled([
      fetchAccounts(),
      fetchReviewQueueCount(),
    ]);
  }, [fetchAccounts, fetchReviewQueueCount]);

  useEffect(() => {
    void fetchAccounts();
    void fetchReviewQueueCount();
  }, [fetchAccounts, fetchReviewQueueCount]);

  const handleConnect = async () => {
    try {
      setActionLoading('connect');
      const authUrl = await connectGmailAccount();
      window.location.href = authUrl;
    } catch (err) {
      setError('Failed to initiate Gmail connection');
      console.error('Error connecting Gmail:', err);
      setActionLoading(null);
    }
  };

  const handleToggleActive = async (account: GmailAccount) => {
    try {
      setActionLoading(account.id);
      if (account.isActive) {
        await deactivateGmailAccount(account.id);
      } else {
        await activateGmailAccount(account.id);
      }
      await refreshSettingsData();
    } catch (err) {
      setError(`Failed to ${account.isActive ? 'pause' : 'resume'} monitoring`);
      console.error('Error toggling account:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisconnect = async (account: GmailAccount) => {
    if (!window.confirm(`Are you sure you want to disconnect ${account.email}?`)) {
      return;
    }

    try {
      setActionLoading(account.id);
      await disconnectGmailAccount(account.id);
      await refreshSettingsData();
    } catch (err) {
      setError('Failed to disconnect Gmail account');
      console.error('Error disconnecting account:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className={embedded ? styles.embeddedContainer : styles.container}>
      {!embedded && (
        <div className={styles.header}>
          <h2 className={styles.title}>Gmail Integration</h2>
          <p className={styles.description}>
            Connect your Gmail accounts to monitor incoming and outgoing emails.
            New emails will be automatically processed and logged.
          </p>
        </div>
      )}

      {error && (
        <div className={styles.error}>
          {error}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            Dismiss
          </Button>
        </div>
      )}

      <SettingsSection title="Connected Accounts">
        {accountsError && (
          <div className={styles.error}>
            {accountsError}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void fetchAccounts();
              }}
              aria-label="Retry loading Gmail accounts"
            >
              Retry
            </Button>
          </div>
        )}

        {accountsLoading && accounts.length === 0 ? (
          <div className={styles.loading}>Loading connected accounts...</div>
        ) : (!accountsError || accounts.length > 0) && (
          <SettingsList emptyMessage="No Gmail accounts connected yet">
            {accounts.map((account) => (
              <SettingsListItem key={account.id}>
                <div className={styles.accountInfo}>
                  <div className={styles.accountEmail}>
                    {account.email}
                    {account.isPrimary && (
                      <span className={styles.primaryBadge}>Primary</span>
                    )}
                  </div>
                  <div className={styles.accountMeta}>
                    <span className={account.isActive ? styles.statusActive : styles.statusInactive}>
                      {account.isActive ? 'Monitoring' : 'Paused'}
                    </span>
                    <span className={styles.lastSync}>
                      Last sync: {formatDate(account.lastSyncAt)}
                    </span>
                  </div>
                </div>
                <div className={styles.accountActions}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleActive(account)}
                    loading={actionLoading === account.id}
                    disabled={actionLoading !== null}
                  >
                    {account.isActive ? 'Pause' : 'Resume'}
                  </Button>
                  {!account.isPrimary && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDisconnect(account)}
                      loading={actionLoading === account.id}
                      disabled={actionLoading !== null}
                    >
                      Disconnect
                    </Button>
                  )}
                </div>
              </SettingsListItem>
            ))}
          </SettingsList>
        )}

        <div className={styles.actions}>
          <Button
            variant="primary"
            onClick={handleConnect}
            loading={actionLoading === 'connect'}
            disabled={actionLoading !== null}
          >
            Connect Gmail Account
          </Button>
        </div>
      </SettingsSection>

      <SettingsSection
        title="Skipped Email Review"
        description="Examine triage-skipped emails in a dedicated review queue instead of inline settings."
      >
        {reviewQueueError && (
          <div className={styles.error}>
            {reviewQueueError}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void fetchReviewQueueCount();
              }}
              aria-label="Retry loading skipped email review queue"
            >
              Retry
            </Button>
          </div>
        )}

        <div className={styles.reviewQueueSummary}>
          <div className={styles.reviewQueueCopy}>
            <span className={styles.reviewQueueLabel}>Awaiting review</span>
            <span className={styles.reviewQueueCount}>
              {reviewQueueLoading ? 'Loading...' : reviewQueueCount ?? '—'}
            </span>
            <p className={styles.reviewQueueHint}>
              Open the review queue to inspect each email, jump out to Gmail, re-run analysis, or dismiss it.
            </p>
          </div>

          <Button
            variant="secondary"
            onClick={() => navigate('/settings/gmail/skipped')}
          >
            Open review queue
          </Button>
        </div>
      </SettingsSection>

      <SettingsSection
        title="How it works"
        description="Gmail integration monitors your inbox and sent mail for new messages"
      >
        <ul className={styles.infoList}>
          <li>Connect your Gmail account using Google OAuth</li>
          <li>We monitor your inbox and sent mail for new messages</li>
          <li>Each new email triggers an action (currently logging for POC)</li>
          <li>Your primary account is automatically enrolled when you log in</li>
          <li>You can connect additional Gmail accounts for monitoring</li>
        </ul>
      </SettingsSection>
    </div>
  );
};

export default GmailSettings;
